import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { LayoutDashboard, Boxes, Calendar, FileText, Receipt, Building2, LifeBuoy, Users, Bell, LogOut, Settings, UserCog, BarChart3 } from "lucide-react";

const NAV = [
  { to: "/", label: "Home", icon: LayoutDashboard, roles: ["super_admin","company_admin","it_admin","auditor","employee"], testid: "nav-dashboard" },
  { to: "/inventory", label: "Inventory", icon: Boxes, roles: ["super_admin","company_admin","it_admin","auditor"], testid: "nav-inventory" },
  { to: "/subscriptions", label: "Subscriptions", icon: Calendar, roles: ["super_admin","company_admin","it_admin","auditor"], testid: "nav-subscriptions" },
  { to: "/audits", label: "Audits", icon: FileText, roles: ["super_admin","company_admin","it_admin","auditor"], testid: "nav-audits" },
  { to: "/invoices", label: "Invoices", icon: Receipt, roles: ["super_admin","company_admin","it_admin","auditor"], testid: "nav-invoices" },
  { to: "/vendors", label: "Vendors / AMC", icon: Building2, roles: ["super_admin","company_admin","it_admin"], testid: "nav-vendors" },
  { to: "/tickets", label: "Support Desk", icon: LifeBuoy, roles: ["super_admin","company_admin","it_admin","employee","auditor"], testid: "nav-tickets" },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["super_admin","company_admin","it_admin","auditor"], testid: "nav-reports" },
  { to: "/users", label: "Team & Users", icon: UserCog, roles: ["super_admin","company_admin"], testid: "nav-users" },
  { to: "/companies", label: "Companies", icon: Settings, roles: ["super_admin"], testid: "nav-companies" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const [notifs, setNotifs] = useState([]);
  const [openNotif, setOpenNotif] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/notifications").then(r => setNotifs(r.data)).catch(() => {});
  }, []);

  const unread = notifs.filter(n => !n.read).length;
  const visibleNav = NAV.filter(i => i.roles.includes(user?.role));

  return (
    <div className="min-h-screen flex bg-background" data-testid="app-shell">
      {/* Sidebar */}
      <aside className="w-60 border-r border-border bg-[#F9F9F8] flex flex-col" data-testid="sidebar">
        <div className="p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-black flex items-center justify-center">
              <div className="w-2.5 h-2.5 bg-[#FF5000]" />
            </div>
            <div>
              <div className="font-head font-black text-sm leading-none tracking-tight">PRIDE GLOBAL</div>
              <div className="data-label mt-1">Admin Central</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-3">
          {visibleNav.map(item => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} end={item.to === "/"} data-testid={item.testid}
                className={({isActive}) => `flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 ${isActive ? "border-[#FF5000] bg-white text-black font-semibold" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-white"}`}>
                <Icon size={16} strokeWidth={1.75}/>
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-border p-4">
          <div className="data-label mb-1">Signed in as</div>
          <div className="font-head font-bold text-sm truncate" data-testid="sidebar-user-name">{user?.full_name}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider mt-0.5">{user?.role?.replace("_"," ")}</div>
          <button onClick={logout} data-testid="logout-btn" className="mt-3 w-full flex items-center gap-2 text-xs text-muted-foreground hover:text-[#FF5000] border border-border px-2 py-1.5 uppercase tracking-wider">
            <LogOut size={12}/> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border bg-white flex items-center justify-between px-6" data-testid="topbar">
          <div>
            <div className="data-label">Command Center</div>
            <div className="font-head font-bold text-base tracking-tight leading-none mt-0.5">
              {user?.company_id ? "Company Workspace" : "Global Super Admin"}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="notif-btn" onClick={() => setOpenNotif(v => !v)} className="relative border border-border px-3 py-1.5 flex items-center gap-2 text-xs uppercase tracking-wider hover:bg-muted">
              <Bell size={14}/> Alerts {unread > 0 && <span className="bg-[#FF5000] text-white font-mono px-1.5 text-[10px]">{unread}</span>}
            </button>
            {openNotif && (
              <div className="absolute right-6 top-14 w-96 bg-white border border-border z-50" data-testid="notif-panel">
                <div className="p-3 border-b border-border flex justify-between items-center">
                  <div className="font-head font-bold text-sm">ALERTS</div>
                  <button data-testid="mark-all-read-btn" onClick={async () => { await api.post("/notifications/read-all"); const r = await api.get("/notifications"); setNotifs(r.data); }} className="data-label hover:text-[#FF5000]">Mark all read</button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifs.length === 0 && <div className="p-4 text-xs text-muted-foreground">No alerts.</div>}
                  {notifs.map(n => (
                    <div key={n.id} className={`p-3 border-b border-border text-xs ${!n.read ? "bg-[#FFF4EB]" : ""}`} data-testid={`notif-item-${n.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="font-semibold">{n.title}</div>
                        <span className={`data-label ${n.severity === "critical" ? "text-[#FF3B30]" : n.severity === "warning" ? "text-[#FF7A00]" : ""}`}>{n.severity}</span>
                      </div>
                      <div className="text-muted-foreground mt-1">{n.body}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-auto" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
